import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { ArrowRight, Stethoscope, LayoutDashboard, ClipboardList } from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';
import MedicinaRevisaoAtestados from './atestados/MedicinaRevisaoAtestados';
import { perfilNoModulo } from '../../utils/perfisDerivados';

// ---------------------------------------------------------------------------
// Módulo Medicina do Trabalho
// ---------------------------------------------------------------------------
// Veio da antiga "Central de Formulários" (Forms.js) e, até 2026-07-28, tinha
// uma aba só — "Formulários" — que abria um SEGUNDO nível com as sub-abas
// Preencher/Relatórios. Dois cliques para chegar em qualquer lugar, e o rótulo
// estava errado: registrar a revisão de um atestado não é preencher formulário.
//
// Agora existe o SUB-MÓDULO "Revisão de Atestados" (padrão do Petty Cash /
// Troco no Financeiro): o card abre um sub-hub com as duas telas do processo —
// Lançamento (o médico registra) e Consulta (Loja → Cargo → Nome).
//
// A Consulta substitui o antigo relatório linha a linha E a página "Revisões
// Atestados" do BI MCDES_16, que será desativada.

const PERMISSIONS = {
    // União dos perfis das duas telas — a permissão FINA (quem lança vs quem
    // só consulta) fica dentro do sub-hub, junto das abas que ela governa.
    // `Manutencao` entra só para CONSULTAR (e só os cargos de manutenção — o
    // recorte é do servidor); lançar continua sendo do médico.
    'revisao': ['Medico', 'RH', 'Gerente', 'Diretoria', 'Admin', 'Manutencao', 'Operacional'],
};

const modulesList = [
    {
        id: 'revisao',
        label: 'Revisão de Atestados',
        icon: ClipboardList,
        // Verde do módulo (ver docs/ARQUITETURA.md › Paleta dos módulos).
        color: '#16a34a',
        bgColor: '#dcfce7',
        desc: 'Lançamento dos atestados e consulta da redução de dias por loja, cargo e pessoa.',
        stat: 'Medicina',
    },
];

const MedicinaPainel = () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const userRole = perfilNoModulo(user?.perfil, 'medicina') || '';

    const [isMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    const allowedModules = modulesList.filter(mod => PERMISSIONS[mod.id]?.includes(userRole) || userRole === 'Admin');

    const OverviewWidgets = () => (
        <div>
            <div style={{
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                borderRadius: '16px', padding: '40px', color: '#fff',
                marginBottom: '32px', position: 'relative', overflow: 'hidden',
                boxShadow: '0 10px 15px -3px rgba(22, 163, 74, 0.3)',
            }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>
                        Medicina do Trabalho
                    </h2>
                    <p style={{ color: '#d3f5dd', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>
                        Registro de afastamentos e acompanhamento do absenteísmo do Grupo Correia MC.
                    </p>
                </div>
                <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.12 }}>
                    <Stethoscope size={250} color="#ffffff" />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                {allowedModules.map((mod) => (
                    <div
                        key={mod.id}
                        onClick={() => setActiveTab(mod.id)}
                        style={{
                            backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
                            border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column',
                            height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden',
                            transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px -2px rgba(0,0,0,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
                    >
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                            <div>
                                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                                <mod.icon size={26} />
                            </div>
                        </div>
                        <div style={{ marginTop: 'auto' }}>
                            <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                                <span>Acessar</span> <ArrowRight size={16} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    useEffect(() => {
        const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Se a aba ativa deixou de ser permitida (troca de perfil), volta ao painel.
    useEffect(() => {
        if (activeTab !== 'dashboard' && !(PERMISSIONS[activeTab]?.includes(userRole) || userRole === 'Admin')) {
            setActiveTab('dashboard');
        }
    }, [activeTab, userRole]);

    const contentBoxStyle = {
        backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0',
        padding: '32px', minHeight: '75vh', width: '100%',
    };

    const renderContent = () => {
        if (activeTab === 'dashboard') return <OverviewWidgets />;
        switch (activeTab) {
            case 'revisao': return <div style={contentBoxStyle}><MedicinaRevisaoAtestados /></div>;
            default: return null;
        }
    };

    const getMarginLeft = () => window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px');

    // Ponto de partida do Manual conforme a aba ativa (o botão mostra o módulo todo).
    const getSecaoAjuda = () => (activeTab === 'dashboard' ? null : 'atestado-medico');

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f7fcf8' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

                {/* CABEÇALHO — padrão dos módulos, na cor da Medicina (verde) */}
                <ModuloHeader
                    eyebrow="Módulo"
                    titulo="Medicina do Trabalho"
                    cor="#16a34a"
                    onAbrirManual={() => setIsHelpOpen(true)}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

                    {/* BARRA DE NAVEGAÇÃO */}
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px',
                        backgroundColor: '#fff', padding: '8px', borderRadius: '12px',
                        border: '1px solid #e2e8f0', width: '100%',
                    }}>
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '10px 20px', borderRadius: '8px', border: 'none',
                                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                background: activeTab === 'dashboard' ? 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)' : 'transparent',
                                color: activeTab === 'dashboard' ? '#fff' : '#64748b',
                                boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(22, 163, 74, 0.3)' : 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <LayoutDashboard size={18} /> Visão Geral
                        </button>

                        <div style={{ minWidth: '1px', backgroundColor: '#e2e8f0', margin: '0 5px' }} />

                        {allowedModules.map((mod) => (
                            <button
                                key={mod.id}
                                onClick={() => setActiveTab(mod.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '10px 20px', borderRadius: '8px', fontWeight: '600',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent',
                                    backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent',
                                    color: activeTab === mod.id ? mod.color : '#64748b',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <mod.icon size={18} /> {mod.label}
                            </button>
                        ))}
                    </div>

                    {renderContent()}
                </div>
            </div>

            <HelpCenter
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                secaoInicial={getSecaoAjuda()}
                modulo="medicina"
            />
        </div>
    );
};

export default MedicinaPainel;
